import { z } from 'zod';
import type { QueryClient } from '@tanstack/react-query';
import { inventoryQuery, transactionsQuery } from '@/api/queries';
import type { ToolConfig } from './utils/create-tool';
import { computeProductVelocities } from './utils/inventory';

const inputSchema = z.object({
  target_weeks_on_hand: z.number().optional().default(4),
  lookback_days: z.number().optional().default(14),
  category: z.string().optional(),
  brand_name: z.string().optional(),
});

async function execute(params: z.infer<typeof inputSchema>, queryClient: QueryClient) {
  const { target_weeks_on_hand, lookback_days, category, brand_name } = params;
  const targetDays = target_weeks_on_hand * 7;

  const toDate = new Date().toISOString().slice(0, 10);
  const fromDate = new Date(Date.now() - lookback_days * 86_400_000).toISOString().slice(0, 10);

  const [inventory, transactions] = await Promise.all([
    queryClient.fetchQuery(inventoryQuery()),
    queryClient.fetchQuery(transactionsQuery(fromDate, toDate)),
  ]);

  let velocities = computeProductVelocities(inventory, transactions, lookback_days);

  if (category) {
    const filter = category.toLowerCase();
    velocities = velocities.filter((p) => p.category?.toLowerCase().includes(filter));
  }

  if (brand_name) {
    const filter = brand_name.toLowerCase();
    velocities = velocities.filter((p) => p.brand_name?.toLowerCase().includes(filter));
  }

  const recommendations = velocities
    .map((p) => {
      const unitGap = targetDays * p.daily_velocity - p.quantity_on_hand;
      const suggestedOrderQty = Math.ceil(Math.max(unitGap, 0));

      return {
        product_id: p.product_id,
        product_name: p.product_name,
        sku: p.sku,
        brand_name: p.brand_name,
        brand_catalog_brand_id: p.brand_catalog_brand_id,
        category: p.category,
        current_quantity: p.quantity_on_hand,
        unit_abbreviation: p.unit_abbreviation,
        unit_cost: p.unit_cost,
        daily_velocity: p.daily_velocity,
        current_days_on_hand: p.projected_days_on_hand,
        target_days: targetDays,
        unit_gap: Math.round(unitGap * 100) / 100,
        suggested_order_qty: suggestedOrderQty,
        urgency: p.urgency,
      };
    })
    .filter((r) => r.unit_gap > 0);

  // Group by brand for PO drafting
  const brandGroups = new Map<
    string,
    {
      brand_name: string | null;
      brand_catalog_brand_id: string | null;
      can_create_po: boolean;
      items: Array<{ product_id: number; product_name: string; suggested_order_qty: number }>;
    }
  >();

  for (const rec of recommendations) {
    const key = rec.brand_name ?? '__no_brand__';
    let group = brandGroups.get(key);
    if (!group) {
      group = {
        brand_name: rec.brand_name,
        brand_catalog_brand_id: rec.brand_catalog_brand_id,
        can_create_po: rec.brand_catalog_brand_id != null,
        items: [],
      };
      brandGroups.set(key, group);
    }
    group.items.push({
      product_id: rec.product_id,
      product_name: rec.product_name,
      suggested_order_qty: rec.suggested_order_qty,
    });
  }

  const by_brand = [...brandGroups.values()]
    .map((g) => ({
      brand_name: g.brand_name,
      brand_catalog_brand_id: g.brand_catalog_brand_id,
      can_create_po: g.can_create_po,
      items: g.items,
      total_items: g.items.length,
    }))
    .sort((a, b) => b.total_items - a.total_items);

  return { target_weeks_on_hand, recommendations, by_brand };
}

export const reorderRecommendationsTool: ToolConfig<typeof inputSchema> = {
  name: 'get_reorder_recommendations',
  description:
    'Calculates reorder recommendations grouped by brand. For each product below the target weeks-on-hand, computes the unit gap and suggested order quantity. Use this when the user asks what to order, wants to maintain a specific stock level, or asks for PO suggestions.',
  inputSchema,
  execute,
};
